import { useEffect, useState } from "react";
import { Puzzle, History } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const STATUS_LABEL = {
  not_requested: "Not requested",
  requested: "Awaiting PathLete® approval",
  approved_by_provider: "Approved — ready to activate",
};

export const PluginsCard = () => {
  const { user } = useAuth();
  const [plugins, setPlugins] = useState([]);

  const load = () => api.get("/plugins").then(({ data }) => setPlugins(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const request = async (p) => {
    const note = window.prompt(`Anything to tell PathLete® about why you need "${p.name}"?`, "");
    if (note === null) return;
    try {
      await api.post("/plugins/request", { plugin_id: p.plugin_id, note });
      toast.success("Request sent to info@pathlete.org");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const activate = async (p, active) => {
    try {
      await api.put(`/plugins/${p.plugin_id}/activate`, { active });
      toast.success(`${p.name} ${active ? "activated" : "deactivated"}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white p-5" data-testid="plugins-card">
      <div className="flex items-start gap-3">
        <Puzzle className="h-5 w-5 text-[#002FA7] mt-0.5" />
        <div>
          <h2 className="font-heading font-bold text-[#111827]">AI Plugins</h2>
          <p className="text-xs text-[#6B7280] mt-0.5">
            Off by default. Request the plugin from PathLete®; once approved, the primary administrator can switch it on.
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {plugins.map((p) => (
          <div key={p.plugin_id} className="rounded-md border border-[#E5E7EB] p-3" data-testid={`plugin-${p.plugin_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-[#111827]">{p.name}</p>
                <p className="text-xs text-[#6B7280] mt-0.5">{p.desc}</p>
                <p className="text-[10px] uppercase tracking-[0.1em] text-[#002FA7] mt-1.5">
                  {STATUS_LABEL[p.status] || p.status}
                </p>
              </div>
              <div className="shrink-0 flex items-center gap-2">
                {p.status === "not_requested" && (
                  <Button size="sm" variant="outline" onClick={() => request(p)} data-testid={`plugin-request-${p.plugin_id}`} className="h-8">
                    Request
                  </Button>
                )}
                {p.status !== "not_requested" && (
                  <Switch checked={p.active} disabled={!user?.is_primary_admin || p.status !== "approved_by_provider"}
                    onCheckedChange={(v) => activate(p, v)} data-testid={`plugin-toggle-${p.plugin_id}`} />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const LoginActivityCard = () => {
  const [rows, setRows] = useState([]);
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [labs, setLabs] = useState([]);
  const [lab, setLab] = useState("");
  const { user } = useAuth();
  const isSuper = !!user?.is_primary_admin;

  const load = (failedOnly, labId) =>
    api.get("/admin/login-activity", {
      params: {
        limit: 200,
        ...(failedOnly ? { success: false } : {}),
        ...(labId ? { lab: labId } : {}),
      },
    }).then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e)));

  useEffect(() => { load(onlyFailed, lab); }, [onlyFailed, lab]);
  useEffect(() => {
    if (!isSuper) return;
    api.get("/labs").then(({ data }) => setLabs(data || []))
      .catch((e) => console.warn("lab list unavailable:", e));
  }, [isSuper]);

  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white p-5" data-testid="login-activity-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <History className="h-5 w-5 text-[#002FA7] mt-0.5" />
          <div>
            <h2 className="font-heading font-bold text-[#111827]">Login Activity</h2>
            <p className="text-xs text-[#6B7280] mt-0.5">
              {isSuper
                ? "Every sign-in and failed attempt across all labs, with IP and device."
                : "Sign-ins and failed attempts for your lab, with IP and device."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isSuper && (
            <select value={lab} onChange={(e) => setLab(e.target.value)} data-testid="login-activity-lab-filter"
              className="h-8 rounded-md border border-[#E5E7EB] bg-white px-2 text-xs">
              <option value="">All labs</option>
              {labs.map((l) => <option key={l.lab_id} value={l.lab_id}>{l.name}</option>)}
            </select>
          )}
          <button onClick={() => setOnlyFailed(!onlyFailed)} data-testid="login-activity-filter-btn"
            className="text-xs text-[#002FA7] hover:underline">
            {onlyFailed ? "Show all" : "Failed only"}
          </button>
        </div>
      </div>

      <div className="mt-4 max-h-80 overflow-y-auto">
        <table className="w-full text-sm" data-testid="login-activity-table">
          <thead className="bg-[#F3F4F6] sticky top-0">
            <tr>
              {["When", "Email", "Result", "IP", "Device"].map((h) => (
                <th key={h} className="text-left font-medium text-[10px] uppercase tracking-[0.08em] text-[#6B7280] px-2 py-1.5">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-2 py-6 text-center text-[#6B7280]" data-testid="login-activity-empty">No activity recorded yet.</td></tr>
            )}
            {rows.map((a) => (
              <tr key={a.activity_id} className="border-t border-[#E5E7EB]">
                <td className="px-2 py-1.5 whitespace-nowrap text-xs">{String(a.at).slice(0, 19).replace("T", " ")}</td>
                <td className="px-2 py-1.5 text-xs truncate max-w-[180px]">{a.email}</td>
                <td className="px-2 py-1.5 text-xs whitespace-nowrap"
                  style={{ color: a.success ? "#2E7D32" : "#C92A2A" }}>
                  {a.success ? (a.reason === "password_ok_awaiting_2fa" ? "2FA pending" : "success") : (a.reason || "failed")}
                </td>
                <td className="px-2 py-1.5 text-xs">{a.ip || "—"}</td>
                <td className="px-2 py-1.5 text-xs truncate max-w-[220px] text-[#6B7280]">{a.user_agent || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
